import { initEngine } from './core/Engine';
import { GameLoop } from './core/GameLoop';
import { setupScene } from './rendering/SceneSetup';
import { setupPostProcessing } from './rendering/PostProcessing';
import { buildDrivingRange } from './rendering/TerrainBuilder';
import { BallRenderer } from './rendering/BallRenderer';
import { ShotTracer } from './rendering/ShotTracer';
import { CameraSystem } from './camera/CameraSystem';
import { ShotSimulator } from './simulation/ShotSimulator';
import { GearTestOrchestrator } from './testing/GearTestOrchestrator';
import { UIManager } from './ui/UIManager';
import { initRapier, markNewFrame } from './physics/RapierWorld';

async function loadDiagnostics(): Promise<void> {
  const [
    flightValidation,
    realShotValidation,
  ] = await Promise.all([
    import('./validation/validateFlight'),
    import('./validation/validateRealShots'),
    import('./validation/validateGearDNA'),
    import('./equipment/GearCalibrator'),
    import('./player/PlayerSwingDNA'),
    import('./fitting/FittingOptimizer'),
  ]);

  try { flightValidation.printValidationReport(); } catch (e) { console.warn('Validation skipped:', e); }
  try { realShotValidation.printRealShotValidation(); } catch (e) { console.warn('Real shot validation skipped:', e); }
}

function diagnosticsEnabled(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('validate');
}

async function main(): Promise<void> {
  // ── Canvas ──
  const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
  if (!canvas) throw new Error('Canvas not found');

  // ── Engine + Scene ──
  const { engine, scene } = initEngine(canvas);

  // ── Rapier WASM init (async) ──
  await initRapier();

  // ── Scene Setup (lights, shadows, skybox) ──
  const shadowGen = setupScene(scene);

  // ── Camera ──
  const cameraSystem = new CameraSystem(scene);

  // ── Post-Processing ──
  setupPostProcessing(scene, cameraSystem.getCamera());

  // ── Terrain ──
  buildDrivingRange(scene, shadowGen);

  // ── Ball ──
  const ballRenderer = new BallRenderer(scene, shadowGen);

  // ── Shot Tracer ──
  const shotTracer = new ShotTracer(scene);

  // ── Simulator ──
  const simulator = new ShotSimulator(ballRenderer, shotTracer, cameraSystem);

  // ── Gear Test Orchestrator ──
  const orchestrator = new GearTestOrchestrator(shotTracer, scene, shadowGen, () => simulator.getEnvironment());

  // ── Game Loop ──
  const gameLoop = new GameLoop();
  gameLoop.setPhysicsUpdate((dt) => {
    markNewFrame();                    // Frame-idempotent Rapier stepping
    simulator.physicsUpdate(dt);
    orchestrator.physicsUpdate(dt);
  });
  gameLoop.setRender((alpha) => {
    simulator.renderUpdate(alpha);
    orchestrator.renderUpdate(alpha);
  });
  gameLoop.start();

  // ── UI ──
  const ui = new UIManager(simulator, shotTracer, cameraSystem, orchestrator, scale => gameLoop.setTimeScale(scale));

  // ── Render Loop ──
  engine.runRenderLoop(() => {
    gameLoop.tick();
    scene.render();
  });

  // ── Ready ──
  ui.hideLoading();

  if (diagnosticsEnabled()) {
    void loadDiagnostics();
  }
}

main().catch((err) => {
  console.error('ELDRICK init failed:', err);
  const loading = document.getElementById('loading');
  if (loading) {
    const p = loading.querySelector('p');
    if (p) p.textContent = `ERROR: ${err.message}`;
  }
});
